import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories, initialProducts } from '../../mocks/products';

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  image: string;
  sizes: string[];
  available: boolean;
  description: string;
}

export default function HomePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const stored = localStorage.getItem('mignonfally_products');
        if (stored) {
          setProducts(JSON.parse(stored));
        } else {
          setProducts(initialProducts);
          // Ne pas stocker dans localStorage si les données sont trop volumineuses
          try {
            localStorage.setItem('mignonfally_products', JSON.stringify(initialProducts));
          } catch (e) {
            console.warn('LocalStorage quota exceeded, using session storage');
          }
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setProducts(initialProducts);
      }
    };
    
    loadProducts();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && product.available;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/f9195bf5ed7777e5d4f9639b66c05e87/d185bafd3eb9956aa2cc41f5ce977164.png" 
                alt="MignonFally_Store Logo" 
                className="h-12 w-12 object-contain"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent" style={{ fontFamily: 'Playfair Display, serif' }}>
                MignonFally_Store
              </h1>
            </div>
            <div className="flex items-center gap-6">
              <a href="#accueil" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors whitespace-nowrap">Accueil</a>
              <a href="#catalogue" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors whitespace-nowrap">Catalogue</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors whitespace-nowrap">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="accueil" className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/f9195bf5ed7777e5d4f9639b66c05e87/a8ed17dafd89d58e1a257047ec9d51a4.jpeg" 
            alt="MignonFally_Store Collection" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              Bienvenue chez MignonFally_Store
            </h2>
            <p className="text-lg md:text-xl text-white mb-8 leading-relaxed drop-shadow-md">
              Découvrez notre collection exclusive de vêtements, accessoires, téléphones et bien plus encore. Des produits de qualité pour tous les styles et toutes les occasions.
            </p>
            <a 
              href="#catalogue" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <span>Découvrir le Catalogue</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Nos Catégories
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {categories.map(category => (
              <div 
                key={category.id}
                className="flex flex-col items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105"
                onClick={() => {
                  setSelectedCategory(category.id);
                  document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-full text-white">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <span className="text-xs font-medium text-gray-700 text-center">{category.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Catalogue Section */}
      <section id="catalogue" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-4xl font-bold text-center mb-12 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Notre Catalogue
          </h3>

          {/* Filters */}
          <div className="mb-12 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-amber-50'
                }`}
              >
                Tous
              </button>
              {categories.slice(0, 6).map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-amber-50'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-sm"
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-amber-600 text-xl"></i>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {filteredProducts.map(product => (
              <div 
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <div className="relative w-full h-80 bg-gray-100">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Disponible
                  </div>
                </div>
                <div className="p-5">
                  <h4 className="font-bold text-lg mb-2 text-gray-800 line-clamp-2">{product.name}</h4>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                      {product.price.toLocaleString()} FCFA
                    </span>
                    <button className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-5 py-2 rounded-full text-sm font-semibold hover:shadow-lg transition-all whitespace-nowrap">
                      Voir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-shopping-bag-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500">Aucun produit trouvé</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold mb-6 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contactez-nous
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            Pour toute question ou commande, n'hésitez pas à nous contacter via WhatsApp
          </p>
          <a 
            href="https://wa.me/2250777132969" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-green-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-600 transition-all hover:shadow-xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-2xl"></i>
            <span>WhatsApp: +225 07 77 13 29 69</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>MignonFally_Store</h4>
              <p className="text-sm text-amber-100">Votre destination pour la mode et les accessoires de qualité</p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Liens Rapides</h4>
              <div className="flex flex-col gap-2">
                <a href="#accueil" className="text-sm text-amber-100 hover:text-white transition-colors cursor-pointer">Accueil</a>
                <a href="#catalogue" className="text-sm text-amber-100 hover:text-white transition-colors cursor-pointer">Catalogue</a>
                <a href="#contact" className="text-sm text-amber-100 hover:text-white transition-colors cursor-pointer">Contact</a>
                <a href="/admin" className="text-sm text-amber-100 hover:text-white transition-colors cursor-pointer">Administration</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Contact</h4>
              <div className="flex flex-col gap-2 text-sm text-amber-100">
                <p>WhatsApp: +225 07 77 13 29 69</p>
                <p>Wave: +225 07 77 13 29 69</p>
              </div>
            </div>
          </div>
          <div className="border-t border-amber-400 pt-6 text-center text-sm text-amber-100">
            <p>&copy; 2025 MignonFally_Store. Tous droits réservés. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
